import { CodeHighlightNode, CodeNode } from '@lexical/code-core';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import type { Transformer } from '@lexical/markdown';
import {
  CHECK_LIST,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  TRANSFORMERS,
} from '@lexical/markdown';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';

import { TextareaTableTransformer } from './textarea-table-transformer';

/**
 * Every markdown feature Lexical supports natively, plus GFM tables.
 *
 * Three deliberate adjustments to Lexical's default `TRANSFORMERS`:
 *
 * 1. The table transformer is prepended so it gets first refusal on
 *    pipe-delimited lines. Lexical ships no table support of its own.
 * 2. CHECK_LIST is added. It is exported by `@lexical/markdown` but deliberately
 *    left out of `TRANSFORMERS`, so `- [ ] todo` would otherwise import as a
 *    plain bullet containing the literal text `[ ] todo`. It has to precede
 *    UNORDERED_LIST: both match a leading `- `, and the first match wins, so the
 *    bullet rule would consume the line before the checkbox rule saw it.
 * 3. ITALIC_UNDERSCORE is hoisted ahead of ITALIC_STAR. Export uses the first
 *    transformer matching a format, and stored values were written as `_text_`,
 *    so leading with underscore stops every save rewriting `_x_` to `*x*`.
 *    Both stay registered, so both still parse on import.
 */
const transformers: Transformer[] = [
  TextareaTableTransformer,
  CHECK_LIST,
  ...TRANSFORMERS.filter(
    transformer => transformer !== ITALIC_UNDERSCORE
  ).flatMap(transformer =>
    transformer === ITALIC_STAR
      ? [ITALIC_UNDERSCORE, ITALIC_STAR]
      : [transformer]
  ),
];

/**
 * Nodes required by the transformers above. Lexical throws when a transformer
 * produces a node the editor was not configured with.
 */
const nodes = [
  CodeHighlightNode,
  CodeNode,
  HeadingNode,
  LinkNode,
  ListItemNode,
  ListNode,
  QuoteNode,
  TableCellNode,
  TableNode,
  TableRowNode,
];

/**
 * `@lexical/markdown` unconditionally escapes markdown control characters when
 * exporting text nodes. From its `exportTextFormat`:
 *
 *   output.replace(/([*_`~])/g, '\\$1')
 *
 * There is no option to disable it. Stored values were written by a pipeline
 * that never escaped, so without inverting this a value like `snake_case_word`
 * gains backslashes on every save, and every other consumer of that markdown
 * (exports, emails, the legacy RTE) renders the backslashes literally.
 */
const unescapeControlCharacters = (markdown: string) =>
  markdown.replaceAll(/\\([*_`~])/g, '$1');

/**
 * Which newline convention a stored value was written in. A `legacy` value
 * spells a paragraph break as a single `\n`; a `modern` value spells it as
 * `\n\n`, the ordinary markdown way.
 *
 * Only the conversion at the edges differs. Both are read with one block per
 * line, which is how the previous editor rendered them: because blocks carry no
 * margin, a block break and a soft line break look the same, so the empty block
 * a `\n\n` produces is what puts a blank line on screen.
 */
export type TextareaFormattingType = 'legacy' | 'modern';

export const TextareaMarkdownUtil = {
  transformers,
  nodes,
  unescapeControlCharacters,
};
